/**
 * Unified EXTREME calendar session debugger — socket, speed, mutations, patches.
 *
 * Enable (înregistrează tot, consolă liniștită):
 *   localStorage.setItem('__ULTRA_SESSION_DEBUG', '1'); location.reload();
 *
 * Verbose (tot în consolă — foarte mult zgomot):
 *   localStorage.setItem('__ULTRA_SESSION_VERBOSE', '1'); location.reload();
 *
 * Console API:
 *   __ultraSession.help()
 *   __ultraSession.printReport()   — full summary for conclusions
 *   __ultraSession.exportJSON()    — copy/paste log to assistant
 *   __ultraSession.copyToClipboard()
 *   __ultraSession.clear()
 *   __ultraSession.auditDom()      — iOS grid/hour/DOM consistency
 *   __ultraSession.renderStats()
 */

import { installUltraRenderAuditApi, setUltraRenderAuditLogger } from "./ultraRenderAudit";
import { installUltraVirtualizationDevApi } from "./ultraVirtualizationDevGuard";

const MAX_LOG = 2000;
const CATEGORIES = {
   SOCKET: { color: "#27ae60", label: "SOCKET" },
   PERF: { color: "#1e8449", label: "PERF" },
   MUTATION: { color: "#e67e22", label: "MUTATION" },
   PATCH: { color: "#8e44ad", label: "PATCH" },
   BUS: { color: "#2980b9", label: "BUS" },
   QUEUE: { color: "#16a085", label: "QUEUE" },
   PIPELINE: { color: "#2c3e50", label: "PIPELINE" },
   RENDER: { color: "#d35400", label: "RENDER" },
   WARN: { color: "#c0392b", label: "WARN" },
};

/** Categories always echoed to console in quiet mode. */
const QUIET_CONSOLE_CATEGORIES = new Set([
   "MUTATION",
   "SOCKET",
   "QUEUE",
   "WARN",
]);

function perfNow() {
   if (typeof performance !== "undefined" && performance.now) {
      return performance.now();
   }
   return Date.now();
}

export function isUltraSessionDebugEnabled() {
   if (typeof window === "undefined") return false;
   if (window.__ULTRA_SESSION_DEBUG === false) return false;
   if (
      window.__ULTRA_SESSION_DEBUG === true ||
      window.__ULTRA_BACKEND_PROBE === true
   ) {
      return true;
   }
   try {
      if (localStorage.getItem("__ULTRA_SESSION_DEBUG") === "0") return false;
      if (
         localStorage.getItem("__ULTRA_SESSION_DEBUG") === "1" ||
         localStorage.getItem("__ULTRA_BACKEND_PROBE") === "1"
      ) {
         return true;
      }
   } catch {}
   return false;
}

export function isUltraSessionVerbose() {
   if (typeof window === "undefined") return false;
   if (window.__ULTRA_SESSION_VERBOSE === true) return true;
   try {
      return localStorage.getItem("__ULTRA_SESSION_VERBOSE") === "1";
   } catch {
      return false;
   }
}

function shouldEchoSessionLogToConsole(category, durationMs) {
   if (isUltraSessionVerbose()) return true;
   if (QUIET_CONSOLE_CATEGORIES.has(category)) return true;
   if (category === "PERF" && durationMs != null && durationMs >= 100) return true;
   return false;
}

function ensureLog() {
   if (typeof window === "undefined") return null;
   if (!window.__ULTRA_SESSION_LOG) {
      window.__ULTRA_SESSION_LOG = [];
      window.__ULTRA_SESSION_MARKS = {};
   }
   return window.__ULTRA_SESSION_LOG;
}

export function ultraSessionMark(name) {
   if (!isUltraSessionDebugEnabled()) return;
   if (typeof window === "undefined") return;
   window.__ULTRA_SESSION_MARKS = window.__ULTRA_SESSION_MARKS || {};
   window.__ULTRA_SESSION_MARKS[String(name)] = perfNow();
}

export function ultraSessionMeasure(name) {
   if (!isUltraSessionDebugEnabled()) return () => {};
   const key = String(name);
   const t0 = perfNow();
   return (extra = null) => {
      const ms = perfNow() - t0;
      ultraSessionLog("PERF", key, extra, { durationMs: ms });
      return ms;
   };
}

export function ultraSessionLog(category, event, data = null, options = {}) {
   if (!isUltraSessionDebugEnabled()) return;
   const log = ensureLog();
   if (!log) return;

   const cat = CATEGORIES[category] || CATEGORIES.PIPELINE;
   const entry = {
      ts: new Date().toISOString(),
      t: Number(perfNow().toFixed(2)),
      category: cat.label,
      event: String(event || ""),
      durationMs:
         options.durationMs != null
            ? Number(Number(options.durationMs).toFixed(2))
            : null,
      data: data ?? null,
   };

   log.push(entry);
   if (log.length > MAX_LOG) log.splice(0, log.length - MAX_LOG);

   if (!shouldEchoSessionLogToConsole(cat.label, entry.durationMs)) return;

   const dur =
      entry.durationMs != null ? ` ${entry.durationMs}ms` : "";
   const style = `color:${cat.color};font-weight:bold`;
   // eslint-disable-next-line no-console
   console.log(
      `%c[ULTRA:${cat.label}] ${entry.event}${dur}`,
      style,
      data ?? "",
   );
}

function summarizeLog(log) {
   const byCategory = {};
   const byEvent = {};
   const slow = [];
   const warnings = [];

   for (const row of log) {
      byCategory[row.category] = (byCategory[row.category] || 0) + 1;
      const key = `${row.category}::${row.event}`;
      byEvent[key] = (byEvent[key] || 0) + 1;
      if (row.durationMs != null && row.durationMs >= 50) {
         slow.push(row);
      }
      if (row.category === "WARN") warnings.push(row);
   }

   slow.sort((a, b) => (b.durationMs || 0) - (a.durationMs || 0));

   return { byCategory, byEvent, slow: slow.slice(0, 30), warnings };
}

function installGlobalApi() {
   if (typeof window === "undefined") return;

   window.__ultraSession = {
      help() {
         // eslint-disable-next-line no-console
         console.log(`
╔══════════════════════════════════════════════════════════════╗
║  ULTRA SESSION DEBUG — tot ce se întâmplă în calendar        ║
╚══════════════════════════════════════════════════════════════╝

ACTIVARE (refresh o dată) — consolă liniștită, tot salvat în buffer:
  localStorage.setItem('__ULTRA_SESSION_DEBUG','1');
  location.reload();

VERBOSE (mult zgomot în consolă — perf, pipeline, websocket):
  localStorage.setItem('__ULTRA_SESSION_VERBOSE','1');
  location.reload();

DEZACTIVARE:
  localStorage.removeItem('__ULTRA_SESSION_DEBUG');
  localStorage.removeItem('__ULTRA_SESSION_VERBOSE');
  localStorage.removeItem('__ULTRA_BACKEND_PROBE');
  localStorage.removeItem('__ULTRA_PERF');
  localStorage.removeItem('__WS_DEBUG');
  location.reload();

COMENZI după acțiuni (create/delete):
  __ultraSession.printReport()     — rezumat
  __ultraSession.copyToClipboard() — trimite logul asistentului
  __ultraSession.table('MUTATION') — doar mutații
  __ultraSession.auditDom()        — număr griduri / ore / overlap iOS
  __ultraSession.guardDom()        — același audit + warning DEV contract
  __ultraSession.renderStats()     — mount/unmount/resync counters

În mod liniștit vezi doar: MUTATION, SOCKET, QUEUE, WARN (+ perf >100ms).
Tot restul e în buffer pentru exportJSON().
`);
      },

      verbose(on = true) {
         window.__ULTRA_SESSION_VERBOSE = !!on;
         try {
            if (on) localStorage.setItem("__ULTRA_SESSION_VERBOSE", "1");
            else localStorage.removeItem("__ULTRA_SESSION_VERBOSE");
         } catch {}
      },

      clear() {
         window.__ULTRA_SESSION_LOG = [];
         window.__ULTRA_SESSION_MARKS = {};
         if (window.__ultraBackendProbe?.clear) window.__ultraBackendProbe.clear();
         if (window.__ultraPerf?.reset) window.__ultraPerf.reset();
         if (Array.isArray(window.__WS_TRACE)) window.__WS_TRACE.length = 0;
      },

      table(category = null, limit = 80) {
         const log = window.__ULTRA_SESSION_LOG || [];
         const filtered = category
            ? log.filter((row) => row.category === String(category).toUpperCase())
            : log;
         // eslint-disable-next-line no-console
         console.table(
            filtered.slice(-limit).map((row) => ({
               ts: row.ts,
               cat: row.category,
               event: row.event,
               ms: row.durationMs ?? "",
               data:
                  row.data && typeof row.data === "object"
                     ? JSON.stringify(row.data).slice(0, 120)
                     : row.data ?? "",
            })),
         );
      },

      exportJSON(pretty = true) {
         const payload = {
            exportedAt: new Date().toISOString(),
            userAgent:
               typeof navigator !== "undefined" ? navigator.userAgent : "",
            url:
               typeof window !== "undefined" && window.location
                  ? window.location.href
                  : "",
            summary: summarizeLog(window.__ULTRA_SESSION_LOG || []),
            sessionLog: window.__ULTRA_SESSION_LOG || [],
            socketProbe: window.__ultraBackendProbeState?.socketEvents || [],
            wsTrace: (window.__WS_TRACE || []).slice(-100),
            perfMarks: window.__ULTRA_SESSION_MARKS || {},
         };
         return pretty ? JSON.stringify(payload, null, 2) : JSON.stringify(payload);
      },

      async copyToClipboard() {
         const text = window.__ultraSession.exportJSON(true);
         try {
            await navigator.clipboard.writeText(text);
            // eslint-disable-next-line no-console
            console.log("%c[ULTRA] Log copiat în clipboard", "color:#16a085;font-weight:bold");
            return true;
         } catch (error) {
            // eslint-disable-next-line no-console
            console.warn("[ULTRA] Clipboard failed — folosește exportJSON() manual", error);
            return false;
         }
      },

      printReport() {
         const log = window.__ULTRA_SESSION_LOG || [];
         const summary = summarizeLog(log);

         // eslint-disable-next-line no-console
         console.group(
            "%c[ULTRA SESSION] Raport complet",
            "font-size:15px;font-weight:bold;color:#2c3e50",
         );
         // eslint-disable-next-line no-console
         console.log("Total evenimente:", log.length);
         // eslint-disable-next-line no-console
         console.table(summary.byCategory);
         if (summary.slow.length) {
            // eslint-disable-next-line no-console
            console.log("%cOperații lente (≥50ms):", "font-weight:bold;color:#c0392b");
            // eslint-disable-next-line no-console
            console.table(
               summary.slow.map((row) => ({
                  event: `${row.category} ${row.event}`,
                  ms: row.durationMs,
                  ts: row.ts,
               })),
            );
         }
         if (window.__ultraBackendProbe?.printReport) {
            window.__ultraBackendProbe.printReport();
         }
         if (window.__ultraPerf?.dump) {
            window.__ultraPerf.dump();
         }
         // eslint-disable-next-line no-console
         console.log(
            "Pentru export complet: __ultraSession.copyToClipboard() sau __ultraSession.exportJSON()",
         );
         // eslint-disable-next-line no-console
         console.groupEnd();
         return { summary, logCount: log.length };
      },
   };

   installUltraRenderAuditApi(window.__ultraSession);
   installUltraVirtualizationDevApi(window.__ultraSession);
   setUltraRenderAuditLogger(ultraSessionLog);
}

export function bootstrapUltraSessionDebug(reduxStore) {
   if (typeof window === "undefined") return;
   installGlobalApi();
   if (reduxStore) window.__REDUX_STORE__ = reduxStore;

   if (!isUltraSessionDebugEnabled()) return;
   if (window.__ULTRA_SESSION_BOOTSTRAPPED) return;
   window.__ULTRA_SESSION_BOOTSTRAPPED = true;

   window.__ULTRA_SESSION_DEBUG = true;

   if (isUltraSessionVerbose()) {
      try {
         localStorage.setItem("__ULTRA_PERF", "1");
         localStorage.setItem("__WS_DEBUG", "1");
         localStorage.setItem("__ULTRA_BACKEND_PROBE", "1");
      } catch {}
      window.__WS_DEBUG = true;
      window.__WS_TRACE_ALL = true;
      if (window.__ultraPerf?.enable) window.__ultraPerf.enable();
   }

   // eslint-disable-next-line no-console
   console.info(
      "%c[ULTRA DEBUG] ON (liniștit) — __ultraSession.printReport() / auditDom() după glitch",
      "color:#8e44ad;font-size:12px",
   );
}

installGlobalApi();
